package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.*
import com.sddamico.models.LeagueYearDetailResponse
import com.sddamico.util.formatLastUpdated
import com.sddamico.components.motion.Ease
import kotlin.js.json
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@InitRoute
fun initResults(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Results"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun ResultsPage() {
    var leagueData by remember { mutableStateOf<LeagueYearDetailResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            leagueData = apiGet("/api/leagueyears/detail?fullResults=true", LeagueYearDetailResponse.serializer())
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load results"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!)
        return
    }

    val data = leagueData
    if (data == null) {
        EmptyState("Season hasn't started yet")
        return
    }

    val scoredTournaments = data.tournaments.filter { it.isScored }
    val activeTournaments = scoredTournaments.filter { it.status == "ACTIVE" }
    val completedTournaments = scoredTournaments.filter { it.status == "COMPLETED" }

    // Scroll to anchor after content loads (e.g. from /schedule "Results" link)
    val ctx = rememberPageContext()
    LaunchedEffect(data) {
        val fragment = ctx.route.fragment
        if (!fragment.isNullOrEmpty()) {
            document.getElementById(fragment!!)?.let { el ->
                el.asDynamic().scrollIntoView(js("{behavior: 'smooth', block: 'start'}"))
                // Ring the card once the scroll settles so it's clear which tournament the link pointed at.
                el.firstElementChild?.asDynamic()?.animate(
                    arrayOf(
                        json("boxShadow" to "0 0 0 2px ${SitePalette.accent}"),
                        json("boxShadow" to "0 0 0 2px transparent"),
                    ),
                    json("duration" to 1400, "delay" to 450, "easing" to Ease.Out.css, "fill" to "backwards"),
                )
            }
        }
    }

    Column(Modifier.fillMaxWidth().gap(1.5.cssRem)) {
        // Header
        Column {
            SpanText(
                "Tournament Results",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(2.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )
            if (data.lastSyncedAt != null) {
                SpanText(
                    "Updated ${formatLastUpdated(data.lastSyncedAt!!)}",
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.textMuted).margin(top = 0.25.cssRem)
                )
            }
        }

        if (activeTournaments.isEmpty() && completedTournaments.isEmpty()) {
            EmptyState("No tournament results yet")
            return@Column
        }

        if (activeTournaments.isNotEmpty()) {
            SpanText(
                "In Progress",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )
            activeTournaments.forEach { tournament ->
                Div(Modifier.fillMaxWidth().toAttrs { id("tournament-${tournament.id}") }) {
                    TournamentCard(tournament)
                }
            }
        }

        if (completedTournaments.isNotEmpty()) {
            SpanText(
                "Completed",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
                    .margin(top = if (activeTournaments.isNotEmpty()) 1.cssRem else 0.px)
            )
            completedTournaments.forEach { tournament ->
                Div(Modifier.fillMaxWidth().toAttrs { id("tournament-${tournament.id}") }) {
                    TournamentCard(tournament)
                }
            }
        }
    }
}
